import classNames from "classnames";
import { useRef, useState, useEffect, useMemo } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import AscButton from "../../AscButton/AscButton";
import AscTabs from "../../AscTabs/AscTabs";
import AscHeading from "../../AscTypography/AscHeading/AscHeading";
import AscParagraph from "../../AscTypography/AscParagraph/AscParagraph";
import AscVideoPlayer from "../../AscVideoPlayer/AscVideoPlayer";
import AscAddModuleFooter from "../AscAddModuleFooter/AscAddModuleFooter";
import AscContentTabs from "./AscContentTabs";

import styles from "./AscAddModuleContent.module.scss";
import { useAscAddModuleContentForm } from "./useAscAddModuleContentForm";
import {
  AdminChallenge,
  AdminModuleContent,
  AdminModuleContentForm,
  Caption,
  CaptionTranslation,
  translationLabel,
} from "@ascendnow/shared";
import { CircleCloseIcon } from "../../AscIcons";

export interface AscAddModuleContentProps {
  className?: string;
  header: string;
  value: number;
  challenges?: AdminChallenge[];
  currentContentObj?: AdminModuleContent;
  currentContentVideo?: File | string;
  currentContentVideoCaptions?: Caption[];
  currentContentFile?: AdminModuleContentForm["file"];
  setValue: (value: number) => void;
  addContent: (
    contentObj: AdminModuleContent,
    video?: File | string,
    file?: AdminModuleContentForm["file"]
  ) => void;
}

const tabs = [
  { id: "dig-deeper", title: "Dig Deeper" },
  { id: "reflection", title: "Reflection" },
  { id: "challenge", title: "Challenge" },
];
function AscAddModuleContent({
  className,
  header,
  value,
  challenges,
  setValue,
}: AscAddModuleContentProps) {
  const [currentTab, setCurrentTab] = useState(tabs[0].id);
  const [videoUrl, setVideoUrl] = useState<string>();
  const [videoCaptions, setVideoCaptions] = useState<CaptionTranslation[]>([]);
  const formik = useAscAddModuleContentForm();
  const [videoError, setVideoError] = useState(""); //TODO: change to formik error, currently encountering issues with formik
  const touched = useMemo(() => {
    return (
      Object.keys(formik.touched)?.filter(
        (key) => formik.touched?.[key as keyof typeof formik.touched]
      ).length > 0
    );
  }, [formik.touched]);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const addVideo = (file: File | undefined) => {
    formik.setFieldValue("video", file);
    formik.setTouched({ video: true });
    setVideoError("");
  };

  const handleVideoChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    const file = event?.target?.files?.[0];
    addVideo(file);
  };

  const handleRemoveVideo = () => {
    formik.setFieldValue("video", undefined);
    formik.setFieldTouched("video", true);
    setVideoError("Video is required!");
    setVideoUrl(undefined);
  };

  const submitAndNext = () => {
    formik.handleSubmit();
    setValue(value + 1);
  };

  const submitAndBack = () => {
    formik.handleSubmit();
    setValue(value - 1);
  };

  useEffect(() => {
    if (!formik.values.video) {
      setVideoUrl("");
      setVideoError("Video is required!");
      return;
    }

    if (typeof formik.values.video === "string") {
      setVideoCaptions(formik.values.videoCaptions || []);
      setVideoUrl(formik.values.video);
      return;
    }

    if (formik.values.video instanceof File) {
      const url: string = URL.createObjectURL(formik.values.video);
      setVideoUrl(url);
      return;
    }
  }, [formik.values.video]);

  return (
    <div
      data-testid="asc-add-module-content"
      className={classNames(styles.ground, className)}
    >
      <AscHeading className={styles.header} level={6}>
        {header}
      </AscHeading>

      <div>
        <AscParagraph className={styles.title}>Add video</AscParagraph>
        <div className={styles.videoUploader}>
          <AscParagraph className={styles.title}>
            First things first! You need a video
          </AscParagraph>
          <AscParagraph className={styles.subtitle}>
            Upload video to get started
          </AscParagraph>
          {!videoUrl && (
            <AscButton
              className={styles.button}
              onClick={() => inputRef.current?.click()}
            >
              Add video
              <input
                ref={inputRef}
                style={{ display: "none" }}
                className={styles.input}
                type="file"
                onChange={handleVideoChange}
                accept=".mov,.mp4"
              />
            </AscButton>
          )}

          {videoUrl && (
            <div className={styles.videoWrapper}>
              <AscButton
                className={styles.removeSingleButton}
                onClick={handleRemoveVideo}
                variant="gray"
                icon={<CircleCloseIcon className={styles.removeButtonIcon} />}
              />
              <AscVideoPlayer
                url={videoUrl}
                captions={videoCaptions?.map((el) => ({
                  label:
                    translationLabel[
                      el?.language as keyof typeof translationLabel
                    ],
                  srclang: el?.language,
                  src: el?.url,
                }))}
              />
            </div>
          )}
        </div>
        {videoError && formik.touched.video && (
          <div className={styles.fieldError}>{videoError}</div>
        )}
      </div>

      <AscTabs
        type="secondary"
        className={styles.tabsGround}
        tabContentClassName={styles.tabContentClassName}
        navigationClassName={styles.navigationClassName}
        selectedId={currentTab}
        onChange={setCurrentTab}
      >
        {tabs.map((tab) => (
          <AscTabs.Tab key={tab.id} id={tab.id} title={tab.title}>
            <AscContentTabs tabId={tab.id} challenges={challenges} />
          </AscTabs.Tab>
        ))}
      </AscTabs>

      <AscAddModuleFooter
        className={styles.footer}
        currentValue={value}
        touched={touched}
        disabled={!formik.isValid && !videoError}
        goNext={submitAndNext}
        goBack={submitAndBack}
      />
    </div>
  );
}

export default function AscAddModuleContentWithFormikWrapper(
  props: AscAddModuleContentProps
) {
  const {
    addContent,
    currentContentObj,
    currentContentFile,
    currentContentVideo,
    currentContentVideoCaptions,
  } = props;
  return (
    <Formik<AdminModuleContentForm>
      initialValues={{
        title: currentContentObj?.digDeep?.title || "",
        description: currentContentObj?.digDeep?.description || "",
        titleReflection: currentContentObj?.reflection?.question || "",
        number: currentContentObj?.reflection?.answerNum || 3,
        challengeId: currentContentObj?.challenge?.challengeId || "",
        chllengeDesc: currentContentObj?.challenge?.description || "",
        video: currentContentVideo,
        file: currentContentFile,
        videoCaptions:
          currentContentVideoCaptions && currentContentVideoCaptions?.length > 0
            ? currentContentVideoCaptions[0].translations
            : [],
      }}
      validationSchema={Yup.object({
        title: Yup.string().required("Dig deeper title is required"),
        description: Yup.string().required("Description is required"),
        titleReflection: Yup.string().required("Reflection title is required"),
        number: Yup.number().required("Number of responses is required"),
        challengeId: Yup.string().required("Challenge is required"),
        chllengeDesc: Yup.string().required("Description is required"),
      })}
      onSubmit={(values) => {
        const contentObj = {
          id: currentContentObj?.id,
          digDeep: {
            title: values.title,
            description: values.description,
          },
          reflection: {
            question: values.titleReflection,
            answerNum: Number(values.number || 3),
          },
          challenge: {
            challengeId: values.challengeId,
            description: values.chllengeDesc,
          },
        };

        addContent(contentObj, values.video, values.file);
      }}
    >
      <Form>
        <AscAddModuleContent {...props} />
      </Form>
    </Formik>
  );
}
